import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import { CollapsibleSection, SlideOverPanel, StatusPill } from '../systemKit';
import { fmtCompact, fmtFull } from './utils';

// VendorLeverage — per-vendor spend + opportunity flags. Shows "where you
// have negotiating room, where you don't." Click a vendor card → drawer
// with the per-item breakdown (which items, prices, who else sells them).

export default function VendorLeverage({
  companyId,
  startDate,
  endDate,
}) {
  const [rows, setRows] = useState(null);
  const [drawerVendor, setDrawerVendor] = useState(null);

  useEffect(() => {
    if (!companyId || !startDate || !endDate) return;
    let cancelled = false;
    setRows(null);
    (async () => {
      const { data } = await supabase.rpc('cost_dashboard_vendor_leverage', {
        p_company_id: companyId,
        p_start_date: startDate,
        p_end_date: endDate,
      });
      if (cancelled) return;
      setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [companyId, startDate, endDate]);

  const totalSpend = useMemo(() => {
    return (rows || []).reduce((sum, r) => sum + Number(r.total_spend || 0), 0);
  }, [rows]);

  const subhead = rows == null
    ? 'loading…'
    : rows.length === 0
      ? 'no vendor data in window'
      : `${rows.length} vendor${rows.length === 1 ? '' : 's'} · ${fmtCompact(totalSpend)} total`;

  return (
    <>
      <CollapsibleSection
        id="cost-dashboard-vendor-leverage"
        title="Vendor leverage"
        count={rows?.length}
        countTone="neutral"
        subhead={subhead}
        defaultExpanded={false}
      >
        {!rows && <Skeleton />}
        {rows && rows.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, borderTop: '1px solid var(--border)' }}>
            No vendor data in this window.
          </div>
        )}
        {rows && rows.length > 0 && (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--border)' }}>
            {rows.map(r => (
              <VendorCard
                key={r.vendor_name}
                row={r}
                totalSpend={totalSpend}
                onOpen={() => setDrawerVendor(r)}
              />
            ))}
          </div>
        )}
      </CollapsibleSection>

      <SlideOverPanel
        open={drawerVendor != null}
        onClose={() => setDrawerVendor(null)}
        title={drawerVendor?.vendor_name?.replace(/\n/g, ' · ') || 'Vendor'}
        subtitle={drawerVendor ? `${fmtCompact(drawerVendor.total_spend)} · ${Number(drawerVendor.basket_share_pct).toFixed(1)}% of basket · ${drawerVendor.invoice_count} invoices` : ''}
        widthPx={560}
      >
        {drawerVendor && (
          <VendorBreakdown
            companyId={companyId}
            vendorName={drawerVendor.vendor_name}
            startDate={startDate}
            endDate={endDate}
          />
        )}
      </SlideOverPanel>
    </>
  );
}

function VendorCard({ row, totalSpend, onOpen }) {
  const sharePct = totalSpend > 0 ? (Number(row.total_spend) / totalSpend) * 100 : 0;
  return (
    <button
      onClick={onOpen}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: 14,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'border-color 120ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {(row.vendor_name || 'Unknown vendor').replace(/\n/g, ' · ')}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {fmtCompact(row.total_spend)} · {sharePct.toFixed(1)}% of basket · {row.invoice_count} invoices
          </div>
        </div>
        <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>›</span>
      </div>

      {/* Spend bar */}
      <div style={{
        height: 6, background: 'var(--bg-subtle)',
        borderRadius: 3, overflow: 'hidden', marginBottom: 10,
      }}>
        <div style={{
          height: '100%',
          width: `${Math.min(100, sharePct)}%`,
          background: 'var(--accent-primary)',
          transition: 'width 240ms ease',
        }} />
      </div>

      {/* Opportunity flags */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {Number(row.cheapest_on_count) > 0 && (
          <StatusPill tone="green">
            Cheapest on {row.cheapest_on_count} item{Number(row.cheapest_on_count) === 1 ? '' : 's'}
          </StatusPill>
        )}
        {Number(row.most_expensive_on_count) > 0 && (
          <StatusPill tone="red">
            Most expensive on {row.most_expensive_on_count}
          </StatusPill>
        )}
        {Number(row.exclusive_item_count) > 0 && (
          <StatusPill tone="amber">
            Exclusive on {row.exclusive_item_count}
          </StatusPill>
        )}
        {Number(row.diversifiable_spend) > 0 && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto', alignSelf: 'center' }}>
            {fmtCompact(row.diversifiable_spend)} diversifiable
          </span>
        )}
      </div>
    </button>
  );
}

// ─── Drawer body ────────────────────────────────────────────────────────────

function VendorBreakdown({ companyId, vendorName, startDate, endDate }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!companyId || !vendorName) return;
    let cancelled = false;
    setRows(null);
    (async () => {
      const { data } = await supabase.rpc('cost_dashboard_vendor_item_breakdown', {
        p_company_id: companyId,
        p_vendor_name: vendorName,
        p_start_date: startDate,
        p_end_date: endDate,
      });
      if (cancelled) return;
      setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [companyId, vendorName, startDate, endDate]);

  const totalSavings = useMemo(() => {
    return (rows || []).reduce((sum, r) => sum + Number(r.potential_savings || 0), 0);
  }, [rows]);

  if (!rows) {
    return <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>;
  }

  if (rows.length === 0) {
    return (
      <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
        No item-level data for this vendor in the window.
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      {totalSavings > 0 && (
        <div style={{
          padding: 12, marginBottom: 16,
          background: 'var(--bg-warning-subtle)',
          border: '1px solid color-mix(in srgb, var(--text-warning) 30%, transparent)',
          borderRadius: 8,
          fontSize: 13,
          color: 'var(--text-primary)',
        }}>
          <strong style={{ color: 'var(--text-warning)' }}>{fmtCompact(totalSavings)}</strong>{' '}
          potential savings if every item moved to its cheapest available vendor in this window.
        </div>
      )}

      <div style={{
        fontSize: 11, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'var(--text-secondary)', marginBottom: 8,
      }}>
        Item breakdown
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 90px 90px 100px',
          background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)',
          fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
          color: 'var(--text-secondary)', padding: '8px 12px',
        }}>
          <div>Item</div>
          <div style={{ textAlign: 'right' }}>This vendor</div>
          <div style={{ textAlign: 'right' }}>Cheapest</div>
          <div style={{ textAlign: 'right' }}>Savings</div>
        </div>
        {rows.map(r => (
          <div key={r.food_item_id} style={{
            display: 'grid', gridTemplateColumns: '2fr 90px 90px 100px',
            padding: '10px 12px',
            borderBottom: '1px solid var(--border)',
            fontSize: 12,
            alignItems: 'center',
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: r.is_cheapest ? 600 : 400 }}>
                {r.item_name}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', gap: 4, marginTop: 2 }}>
                {r.is_cheapest && <span style={{ color: 'var(--text-success)' }}>cheapest here</span>}
                {r.is_only_supplier && <span style={{ color: 'var(--text-warning)' }}>only supplier</span>}
                {!r.is_cheapest && !r.is_only_supplier && (
                  <span>cheapest: {(r.cheapest_vendor || '—').replace(/\n/g, ' · ')}</span>
                )}
              </div>
            </div>
            <div style={{ textAlign: 'right', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
              {r.vendor_avg_price != null ? fmtFull(r.vendor_avg_price) : '—'}
            </div>
            <div style={{ textAlign: 'right', color: r.is_cheapest ? 'var(--text-success)' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
              {r.cheapest_avg_price != null ? fmtFull(r.cheapest_avg_price) : '—'}
            </div>
            <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600,
              color: Number(r.potential_savings) > 0 ? 'var(--text-warning)' : 'var(--text-muted)',
            }}>
              {Number(r.potential_savings) > 0 ? fmtCompact(r.potential_savings) : '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--border)' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          padding: 14,
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}>
          <div style={{ height: 14, width: '40%', background: 'var(--bg-subtle)', borderRadius: 4, marginBottom: 8 }} />
          <div style={{ height: 6, width: '60%', background: 'var(--bg-subtle)', borderRadius: 3, marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ height: 18, width: 100, background: 'var(--bg-subtle)', borderRadius: 6 }} />
            <div style={{ height: 18, width: 80, background: 'var(--bg-subtle)', borderRadius: 6 }} />
          </div>
        </div>
      ))}
    </div>
  );
}
